/* ===========================================================================
   MM — HEADER / NAVIGATION
   ---------------------------------------------------------------------------
   Trois comportements, un seul fichier :

     1. l'état « scrollé » de la pilule (le verre qui apparaît) ;
     2. les volets desktop — survol, clavier, morphologie du cadre, transition
        d'un volet à l'autre, image d'expertise au survol ;
     3. le tiroir mobile — ouverture et, surtout, verrou de défilement.

   VERROU DE DÉFILEMENT MOBILE. Le bug corrigé ici : le menu s'ouvrait mais
   le doigt faisait défiler la page derrière au lieu du menu. Deux causes,
   traitées ensemble.
     · Le backdrop-filter posé sur <header> en créait un bloc conteneur : le
       tiroir en position fixed se calait sur la barre, pas sur la fenêtre, et
       sa hauteur calculée débordait l'écran. Réglé en CSS — le filtre est
       descendu sur la pilule, et en mobile <header> devient un calque
       inset:0 dans lequel tout est positionné en absolute.
     · Rien n'immobilisait le document. On pose ici .stop-scrolling sur <html>
       — la classe que le thème utilise déjà pour le panier et les filtres, et
       que mm-opti-gsi.js surveille pour mettre Lenis en pause. Le tiroir
       porte data-lenis-prevent et overscroll-behavior: contain, donc il garde
       son propre défilement sans le passer à la page.
   =========================================================================== */

(function () {
	'use strict';

	var SCROLL_THRESHOLD = 24;   /* le verre apparaît dès que le contenu glisse dessous */
	var OPEN_DELAY = 60;         /* survol : évite l'ouverture sur un simple passage */
	var CLOSE_DELAY = 180;       /* survol : laisse traverser la gouttière pilule → volet */

	var desktop = window.matchMedia('(min-width: 769px)');
	var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

	function init(root) {
		var header = root || document.querySelector('header.mm-section-header');
		if (!header || header.dataset.mmHeaderReady === '1') return;
		header.dataset.mmHeaderReady = '1';

		bindScrollState(header);
		bindPanels(header);
		bindMobile(header);
	}

	/* -----------------------------------------------------------------------
	   1. ÉTAT SCROLLÉ
	   ----------------------------------------------------------------------- */

	function bindScrollState(header) {
		var ticking = false;

		function apply() {
			ticking = false;
			header.classList.toggle('is-scrolled', window.scrollY > SCROLL_THRESHOLD);
		}
		function onScroll() {
			if (ticking) return;
			ticking = true;
			requestAnimationFrame(apply);
		}

		window.addEventListener('scroll', onScroll, { passive: true });
		apply();
	}

	/* -----------------------------------------------------------------------
	   2. VOLETS DESKTOP
	   ----------------------------------------------------------------------- */

	function bindPanels(header) {
		var stage = header.querySelector('[data-nav-stage]');
		var box = header.querySelector('[data-nav-box]');
		if (!stage || !box) return;

		var items = Array.prototype.slice.call(header.querySelectorAll('.mm-hd__item[data-panel]'));
		var panels = {};
		Array.prototype.forEach.call(box.querySelectorAll('[data-panel-name]'), function (panel) {
			panels[panel.dataset.panelName] = panel;
		});
		if (!items.length) return;

		/* Le voile vit sur <body> : <header> est une boîte fit-content centrée
		   par le flux, et le calque doit couvrir toute la fenêtre sans dépendre
		   de ce qui sera un jour posé sur le header. */
		var scrim = document.querySelector('.mm-nav-scrim');
		if (!scrim) {
			scrim = document.createElement('div');
			scrim.className = 'mm-nav-scrim';
			document.body.appendChild(scrim);
		}

		var current = null;
		var openTimer = null;
		var closeTimer = null;
		var collapseTimer = null;

		/* Durée lue dans le CSS : une seule source pour le mouvement. */
		function duration() {
			var value = parseFloat(getComputedStyle(header).getPropertyValue('--hd-dur'));
			return isNaN(value) ? 460 : value * 1000;
		}

		function indexOfPanel(name) {
			for (var i = 0; i < items.length; i++) {
				if (items[i].dataset.panel === name) return i;
			}
			return -1;
		}

		/* La hauteur de la boîte est mesurée, pas écrite en dur : le contenu
		   vient de l'éditeur de thème. `offsetHeight - clientHeight` récupère
		   l'épaisseur des filets, la boîte étant en box-sizing: border-box.

		   `instant` distingue les deux gestes. À l'ouverture, la boîte prend
		   sa taille sans transition et seuls le fondu et la levée se voient :
		   un volet qui se déroulerait aussi en hauteur ferait trois mouvements
		   simultanés. C'est en passant d'un volet à l'autre que la hauteur
		   s'anime — là, la déformation du cadre EST la transition. */
		function sizeTo(panel, instant) {
			var chrome = box.offsetHeight - box.clientHeight;
			var next = (panel.offsetHeight + chrome) + 'px';
			if (!instant) {
				box.style.height = next;
				return;
			}
			box.style.transition = 'none';
			box.style.height = next;
			void box.offsetHeight;   /* force le recalcul avant de rendre la main */
			box.style.transition = '';
		}

		function open(name) {
			var panel = panels[name];
			if (!panel || current === name) return;

			var from = current ? indexOfPanel(current) : -1;
			var to = indexOfPanel(name);
			/* Le volet entrant arrive du côté d'où vient la souris dans la nav :
			   passer d'Expertises à Partners pousse vers la gauche, l'inverse
			   vers la droite. C'est ce qui fait lire la transition comme un
			   déplacement et pas comme un simple fondu. */
			var dir = from === -1 ? 0 : (to > from ? 1 : -1);

			Object.keys(panels).forEach(function (key) {
				var p = panels[key];
				if (key === name) return;
				p.style.setProperty('--dir', String(-dir || 1));
				p.classList.remove('is-active');
				pauseMedia(p);
			});

			panel.style.setProperty('--dir', String(dir || 1));
			hydrate(panel);
			clearTimeout(collapseTimer);
			sizeTo(panel, from === -1);
			panel.classList.add('is-active');
			playMedia(panel);

			items.forEach(function (item) {
				item.classList.toggle('is-active', item.dataset.panel === name);
				var trigger = item.querySelector('[aria-expanded]');
				if (trigger) trigger.setAttribute('aria-expanded', String(item.dataset.panel === name));
			});

			header.classList.add('is-open');
			header.dataset.openPanel = name;
			scrim.classList.add('is-visible');
			current = name;
		}

		function close() {
			if (!current) return;
			Object.keys(panels).forEach(function (key) {
				panels[key].classList.remove('is-active');
				pauseMedia(panels[key]);
			});
			items.forEach(function (item) {
				item.classList.remove('is-active');
				var trigger = item.querySelector('[aria-expanded]');
				if (trigger) trigger.setAttribute('aria-expanded', 'false');
			});
			header.classList.remove('is-open');
			delete header.dataset.openPanel;
			scrim.classList.remove('is-visible');
			/* La boîte garde sa hauteur le temps du fondu, puis retombe à zéro
			   sans transition : la refermer en même temps qu'elle disparaît
			   rognerait son contenu à vue. */
			clearTimeout(collapseTimer);
			collapseTimer = setTimeout(function () {
				box.style.transition = 'none';
				box.style.height = '0px';
				void box.offsetHeight;
				box.style.transition = '';
			}, duration());
			current = null;
		}

		/* Le premier element focusable du volet. Utilise a chaque ouverture
		   clavier ou activation : voir les gestionnaires click et keydown. */
		function focusPanel(name) {
			var first = panels[name] && panels[name].querySelector('a[href], button');
			if (first) first.focus();
		}

		function scheduleOpen(name) {
			clearTimeout(closeTimer);
			clearTimeout(openTimer);
			/* Un volet déjà ouvert : on bascule tout de suite, sans latence —
			   c'est le glissement d'un volet à l'autre, pas une ouverture. */
			if (current) { open(name); return; }
			openTimer = setTimeout(function () { open(name); }, OPEN_DELAY);
		}

		function scheduleClose() {
			clearTimeout(openTimer);
			clearTimeout(closeTimer);
			closeTimer = setTimeout(function () {
				/* Le padding de la pilule fait partie du trajet vers le volet. */
				if (!header.matches(':hover')) close();
			}, CLOSE_DELAY);
		}

		items.forEach(function (item) {
			var name = item.dataset.panel;
			if (!panels[name]) return;

			var trigger = item.querySelector('a, button');

			item.addEventListener('mouseenter', function () {
				if (!desktop.matches || !canHover.matches) return;
				scheduleOpen(name);
			});
			item.addEventListener('mouseleave', function () {
				if (!desktop.matches || !canHover.matches) return;
				scheduleClose();
			});

			if (!trigger) return;

			/* L'ACTIVATION VIT DANS click, PAS DANS keydown, ET C'EST LE POINT
			   D'ACCESSIBILITÉ CENTRAL. Entrée sur un lien produit un click
			   synthétique ; surtout, les lecteurs d'écran en mode navigation
			   (NVDA, JAWS, VoiceOver) activent par un click simulé et
			   n'émettent JAMAIS de keydown : une logique d'ouverture posée
			   sur keydown leur est invisible. La première version filtrait en
			   plus sur canHover, donc sur tout poste équipé d'une souris le
			   click des lecteurs d'écran suivait le lien : le méga menu leur
			   restait fermé, avec un aria-expanded qui promettait un dépliage
			   qu'aucun de leurs gestes ne pouvait produire (relevé par
			   l'audit). Ici : premier appui ouvre, second suit le lien,
			   aria-expanded annonce lequel est lequel.

			   À la souris, rien ne change : le survol a déjà ouvert, donc
			   current === name et le clic navigue. Seule micro-différence, un
			   clic pendant les 60 ms du délai d'ouverture ouvre au lieu de
			   naviguer, ce qui est l'intention du geste.

			   Les modificateurs passent au navigateur : Cmd, Ctrl, Maj ou Alt
			   plus Entrée ou clic, c'est « ouvre la destination ailleurs »,
			   pas « déplie le volet ». */
			trigger.addEventListener('click', function (event) {
				if (!desktop.matches) return;
				if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
				if (current === name) return;
				event.preventDefault();
				open(name);
				focusPanel(name);
			});

			/* LES TOUCHES QUI NE PRODUISENT PAS DE click. Entrée n'est pas
			   ici : sur un lien, elle produit un click synthétique et passe
			   par le gestionnaire ci-dessus, le même chemin que la souris,
			   le tactile et les lecteurs d'écran. Ne restent que les touches
			   sans équivalent natif :

			     Espace     ouvre ou ferme, jamais de défilement ;
			     ArrowDown  ouvre et descend sur le premier lien ;
			     ArrowUp    referme (Échap aussi, plus haut, avec retour
			                de focus sur le déclencheur).

			   À CHAQUE OUVERTURE, LE FOCUS ENTRE DANS LE VOLET. Les volets
			   vivent après TOUS les déclencheurs dans le DOM : laisser le
			   focus sur le lien, comme le voudrait le pattern disclosure
			   canonique, rendait le contenu inatteignable au Tab, chaque
			   déclencheur traversé permutant le volet ouvert (relevé par
			   l'audit). L'APG admet ce déplacement quand le contenu déplié
			   n'est pas adjacent, et ArrowDown le faisait déjà. */
			trigger.addEventListener('keydown', function (event) {
				if (!desktop.matches) return;
				if (event.key === 'ArrowDown') {
					event.preventDefault();
					open(name);
					focusPanel(name);
				} else if (event.key === 'ArrowUp') {
					if (current !== name) return;
					event.preventDefault();
					close();
				} else if (event.key === ' ' || event.key === 'Spacebar') {
					event.preventDefault();
					if (current === name) { close(); return; }
					open(name);
					focusPanel(name);
				}
			});

			/* PAS D'OUVERTURE AU focus. L'ancienne permutation (un volet
			   ouvert, le focus arrive sur un autre déclencheur, son volet
			   s'ouvre) rendait le premier Entrée imprévisible et, combinée au
			   Tab, fermait le volet qu'on venait d'ouvrir avant d'avoir pu
			   l'atteindre. Le survol garde ce glissement via mouseenter ; au
			   clavier, chaque déclencheur attend une activation. */
		});

		/* La sortie d'un lien ne vaut pas sortie du menu : conserver aussi
		   les marges de la pilule et le pont vers le volet dans la zone active. */
		header.addEventListener('mouseenter', function () {
			if (!desktop.matches || !canHover.matches) return;
			clearTimeout(closeTimer);
		});
		header.addEventListener('mouseleave', function () {
			if (!desktop.matches || !canHover.matches) return;
			scheduleClose();
		});

		stage.addEventListener('mouseenter', function () {
			if (!desktop.matches || !canHover.matches) return;
			clearTimeout(closeTimer);
		});
		stage.addEventListener('mouseleave', function () {
			if (!desktop.matches || !canHover.matches) return;
			scheduleClose();
		});

		scrim.addEventListener('click', close);

		header.addEventListener('keydown', function (event) {
			if (event.key !== 'Escape' || !current) return;
			var name = current;
			close();
			var item = items[indexOfPanel(name)];
			var trigger = item && item.querySelector('a, button');
			if (trigger) trigger.focus();
		});

		/* Quitter le header au clavier referme : le volet ne doit pas rester
		   ouvert dans le dos de l'utilisateur. */
		header.addEventListener('focusout', function (event) {
			if (!current) return;
			if (event.relatedTarget && header.contains(event.relatedTarget)) return;
			close();
		});

		window.addEventListener('resize', function () {
			if (!desktop.matches) close();
			else if (current) sizeTo(panels[current]);
		});

		bindExpertises(box, 'expertises');
		bindExpertises(box, 'outils');
	}

	/* -----------------------------------------------------------------------
	   2b. Volets Expertises et Outils gratuits — nom survolé, image
	   correspondante. Le second reprend le balisage du premier.
	   ----------------------------------------------------------------------- */

	function bindExpertises(box, name) {
		var panel = box.querySelector('[data-panel-name="' + name + '"]');
		if (!panel) return;

		var rows = Array.prototype.slice.call(panel.querySelectorAll('.mm-nav-exp__row'));
		var images = Array.prototype.slice.call(panel.querySelectorAll('.mm-nav-exp__img'));
		var marker = panel.querySelector('.mm-nav-exp__marker');
		if (!rows.length) return;

		function activate(index) {
			rows.forEach(function (row, i) { row.classList.toggle('is-active', i === index); });
			images.forEach(function (img, i) { img.classList.toggle('is-active', i === index); });
			if (!marker) return;
			var row = rows[index];
			/* Le repère se centre sur la ligne réelle : le nombre d'expertises
			   et la hauteur de police peuvent changer sans retoucher le CSS. */
			var y = row.offsetTop + (row.offsetHeight / 2) - (marker.offsetHeight / 2);
			marker.style.setProperty('--y', Math.round(y) + 'px');
			/* En deux colonnes, le repere suit aussi la colonne et sa largeur.
			   La ligne deborde de 24px a gauche (marge negative) comme le repere :
			   on compense pour que les deux restent alignes. */
			marker.style.setProperty('--x', Math.round(row.offsetLeft + 24) + 'px');
			marker.style.setProperty('--w', Math.round(row.offsetWidth) + 'px');
		}

		rows.forEach(function (row, i) {
			row.addEventListener('mouseenter', function () { activate(i); });
			row.addEventListener('focus', function () { activate(i); });
		});

		activate(0);
		/* Les polices custom décalent les lignes en arrivant : on recale le
		   repère une fois qu'Aeonik est posée. */
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(function () {
				var active = rows.findIndex ? rows.findIndex(function (r) { return r.classList.contains('is-active'); }) : 0;
				activate(active < 0 ? 0 : active);
			});
		}
	}

	/* -----------------------------------------------------------------------
	   3. TIROIR MOBILE
	   ----------------------------------------------------------------------- */

	/* Piège de tabulation partagé. Le tiroir et la feuille de langue se
	   déclarent tous deux modaux : la tabulation doit y rester enfermée,
	   sinon le focus part dans une page que .stop-scrolling empêche de faire
	   défiler. Rien à l'écran ne bouge — seul l'ordre du Tab change, et
	   uniquement pendant que le panneau est ouvert. WCAG 2.1.2. */
	function piegerTab(panel) {
		if (!panel) return;
		panel.addEventListener('keydown', function (event) {
			if (event.key !== 'Tab') return;
			var focusables = panel.querySelectorAll('button, [href], select, [tabindex]:not([tabindex="-1"])');
			if (!focusables.length) return;
			var first = focusables[0];
			var last = focusables[focusables.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		});
	}

	function bindMobile(header) {
		var burger = header.querySelector('[data-menu-toggle]');
		var drawer = header.querySelector('[data-mobile-menu]');
		var scrim = header.querySelector('[data-mobile-scrim]');
		if (!burger || !drawer) return;

		var root = document.documentElement;
		var scroller = createScroller(drawer, drawer.querySelector('[data-mobile-scroll]'));
		var sheet = bindLangSheet(header, scroller);
		bindRailDrag(drawer);
		piegerTab(drawer);

		function isOpen() {
			return header.classList.contains('is-menu-open');
		}

		function setOpen(open) {
			header.classList.toggle('is-menu-open', open);
			burger.setAttribute('aria-expanded', String(open));
			/* .stop-scrolling : verrou déjà utilisé par le panier et les
			   filtres, surveillé par mm-opti-gsi.js qui met Lenis en pause. */
			root.classList.toggle('stop-scrolling', open);
			if (open) {
				hydrate(drawer);
				playMedia(drawer);
				if (scroller) scroller.start();
				/* Sans ça le focus reste sur le burger, derrière le voile : la
				   première tabulation repartait dans une page qu'on ne peut plus
				   faire défiler. preventScroll : le tiroir ne doit pas sauter.
				   Aucun anneau à la souris ni au doigt, :focus-visible ne se
				   déclenche qu'après une interaction clavier. */
				var premier = drawer.querySelector('a[href], button');
				if (premier) premier.focus({ preventScroll: true });
			} else {
				if (sheet) sheet.close(true);
				if (scroller) scroller.stop();
				pauseMedia(drawer);
				drawer.scrollTop = 0;
			}
		}

		burger.addEventListener('click', function () { setOpen(!isOpen()); });
		if (scrim) scrim.addEventListener('click', function () { setOpen(false); burger.focus(); });

		drawer.addEventListener('click', function (event) {
			if (event.target.closest('a[href]')) setOpen(false);
		});

		document.addEventListener('keydown', function (event) {
			if (event.key !== 'Escape') return;
			/* La feuille se referme avant le tiroir : elle est au-dessus, c'est
			   elle que l'utilisateur vise. */
			if (sheet && sheet.isOpen()) { sheet.close(); return; }
			if (isOpen()) {
				setOpen(false);
				burger.focus();
			}
		});

		/* Rotation de l'écran ou passage en desktop : on relâche le verrou,
		   sinon la page reste bloquée sur un menu qui n'est plus affiché. */
		desktop.addEventListener('change', function (event) {
			if (event.matches && isOpen()) setOpen(false);
		});
	}

	/* -----------------------------------------------------------------------
	   3b. Feuille de langue
	   ---------------------------------------------------------------------
	   Les options sont de vrais boutons de soumission du formulaire de
	   localisation Shopify : le clic part au serveur, il n'y a aucun état à
	   recopier ni à resynchroniser côté JS. Ici on ne gère que l'ouverture.
	   ----------------------------------------------------------------------- */

	function bindLangSheet(header, scroller) {
		var sheet = header.querySelector('[data-lang-sheet]');
		var trigger = header.querySelector('[data-lang-open]');
		if (!sheet || !trigger) return null;

		function isOpen() {
			return header.classList.contains('is-lang-open');
		}

		function open() {
			header.classList.add('is-lang-open');
			trigger.setAttribute('aria-expanded', 'true');
			/* Le tiroir ne doit pas glisser sous la feuille pendant qu'on
			   choisit : on met son défilement lissé en pause. */
			if (scroller) scroller.pause();
			var current = sheet.querySelector('.mm-sheet__opt.is-current') || sheet.querySelector('.mm-sheet__opt');
			if (current) current.focus();
		}

		function close(silent) {
			if (!isOpen()) return;
			header.classList.remove('is-lang-open');
			trigger.setAttribute('aria-expanded', 'false');
			if (scroller) scroller.resume();
			if (!silent) trigger.focus();
		}

		trigger.addEventListener('click', function () {
			if (isOpen()) close(); else open();
		});

		Array.prototype.forEach.call(sheet.querySelectorAll('[data-lang-close]'), function (node) {
			node.addEventListener('click', function () { close(); });
		});

		/* La feuille se déclare aria-modal : il faut alors que la tabulation y
		   reste enfermée, sinon le lecteur d'écran annonce une boîte modale
		   pendant que le focus part se promener dans le tiroir derrière. */
		piegerTab(sheet.querySelector('.mm-sheet__panel'));

		return { isOpen: isOpen, open: open, close: close };
	}

	/* -----------------------------------------------------------------------
	   DÉFILEMENT LISSÉ DU TIROIR
	   ---------------------------------------------------------------------
	   Une seconde instance Lenis, montée sur le tiroir et non sur le document.
	   Elle est légitime ici : Lenis inspecte data-lenis-prevent seulement
	   jusqu'à son propre rootElement, donc l'attribut posé sur le tiroir
	   n'écarte que l'instance du document — les deux cohabitent sans se
	   marcher dessus.

	   La boucle rAF ne tourne que menu ouvert. Si Lenis n'est pas là, ou si
	   l'utilisateur demande moins de mouvement, on ne monte rien : le
	   défilement natif du tiroir reprend la main, avec son inertie système.

	   PAS DE syncTouch, ET C'EST VOULU. Lenis sait lisser le tactile, mais
	   pour ça il doit annuler les touchmove — et le navigateur refuse dès
	   qu'un défilement est déjà en cours. Vérifié dans le navigateur en
	   émulation tactile : « Ignored attempt to cancel a touchmove event with
	   cancelable=false » à chaque geste. Concrètement, la lib se bat avec le
	   défilement du système et le doigt s'en ressent.

	   Au doigt, l'inertie native EST le défilement lissé, et c'est celle que
	   la main attend — c'est d'ailleurs le réglage du reste du site, l'instance
	   du document tourne aussi sans syncTouch. Ce qu'on lisse ici, c'est la
	   molette et le pavé tactile, qui sans ça avancent par crans dans un
	   panneau de 350px de large. Pour tenter le lissage tactile malgré tout :
	   syncTouch: true, et remettre syncTouchLerp / touchInertiaMultiplier.
	   ----------------------------------------------------------------------- */

	function createScroller(wrapper, content) {
		if (!wrapper || !content) return null;
		if (typeof Lenis === 'undefined') return null;
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;

		var lenis;
		try {
			lenis = new Lenis({
				wrapper: wrapper,
				content: content,
				orientation: 'vertical',
				gestureOrientation: 'vertical',
				smoothWheel: true,
				syncTouch: false,
				lerp: 0.11
			});
		} catch (error) {
			return null;   /* une lib absente ou d'une autre version ne doit pas casser le menu */
		}

		var frame = null;

		function loop(time) {
			lenis.raf(time);
			frame = requestAnimationFrame(loop);
		}

		return {
			start: function () {
				lenis.resize();
				lenis.start();
				if (frame === null) frame = requestAnimationFrame(loop);
			},
			stop: function () {
				lenis.scrollTo(0, { immediate: true, force: true });
				lenis.stop();
				if (frame !== null) {
					cancelAnimationFrame(frame);
					frame = null;
				}
			},
			pause: function () { lenis.stop(); },
			resume: function () { lenis.start(); }
		};
	}

	/* -----------------------------------------------------------------------
	   RAILS : DRAG À LA SOURIS
	   ---------------------------------------------------------------------
	   Le rail des projets défile nativement au doigt — mais à la souris, un
	   overflow CSS ne se tire pas : fenêtre étroite sur desktop, et Safari
	   en mode responsive ne traduit pas le geste souris en tactile. Ici on
	   ne s'occupe DONC QUE du pointeur « mouse » : le tactile garde son
	   inertie système et son snap, on ne se met pas en travers.

	   Pendant le tirage, le snap et le smooth sont suspendus (ils
	   recaleraient la position sous la main) puis restaurés une fois
	   l'inertie posée. Un vrai tirage avale le clic qui suit : les cartes
	   sont des liens, et le tiroir se referme sur un clic de lien.
	   ----------------------------------------------------------------------- */

	function bindRailDrag(scope) {
		var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

		Array.prototype.forEach.call(scope.querySelectorAll('.mm-drawer__rail'), function (rail) {
			var dragging = false;
			var dragged = false;
			var startX = 0;
			var startScroll = 0;
			var lastX = 0;
			var lastT = 0;
			var velocity = 0;
			var momentum = null;

			rail.style.cursor = 'grab';

			/* Un tirage ne doit jamais devenir un glisser-déposer : liens et
			   images sont draggables par défaut, et le navigateur partait
			   avec le lien ou l'image fantôme au lieu de faire défiler.
			   Personne ne dépose une carte de menu ailleurs : on coupe à la
			   source. Le pendant CSS (user-drag/user-select) vit dans
			   mm-header.css, bloc du rail. */
			rail.addEventListener('dragstart', function (event) {
				event.preventDefault();
			});

			function freeze() {
				rail.style.scrollSnapType = 'none';
				rail.style.scrollBehavior = 'auto';
			}
			function thaw() {
				rail.style.scrollSnapType = '';
				rail.style.scrollBehavior = '';
			}

			rail.addEventListener('pointerdown', function (event) {
				if (event.pointerType !== 'mouse' || event.button !== 0) return;
				if (momentum !== null) { cancelAnimationFrame(momentum); momentum = null; }
				dragging = true;
				dragged = false;
				startX = event.clientX;
				lastX = event.clientX;
				lastT = event.timeStamp;
				startScroll = rail.scrollLeft;
				velocity = 0;
				freeze();
				rail.style.cursor = 'grabbing';
			});

			rail.addEventListener('pointermove', function (event) {
				if (!dragging) return;
				var dx = event.clientX - startX;
				if (dragged === false && dx > -4 && dx < 4) return;
				if (dragged === false) {
					/* La capture ne se pose QU'ICI, quand le geste devient un
					   vrai tirage. Posée dès le pointerdown, elle avalait tous
					   les clics : dans Chrome, le click suit la capture et
					   atterrit sur le rail au lieu du lien — cartes mortes.
					   Elle garde le tirage vivant quand la souris sort du rail. */
					try { rail.setPointerCapture(event.pointerId); } catch (error) {}
				}
				dragged = true;
				/* Vélocité RAPPORTÉE AU TEMPS (px par frame de 16,7ms), lissée.
				   Le delta brut entre deux events dépend de la cadence de la
				   souris : il rendait l'inertie erratique — molle sur un geste
				   vif haché, brutale sur un event isolé. */
				var now = event.timeStamp;
				var dt = now - lastT;
				if (dt > 0) {
					var instant = (lastX - event.clientX) / dt * 16.7;
					velocity = velocity * 0.2 + instant * 0.8;
				}
				lastX = event.clientX;
				lastT = now;
				rail.scrollLeft = startScroll - dx;
			});

			function release() {
				if (!dragging) return;
				dragging = false;
				rail.style.cursor = 'grab';
				if (reduced.matches) { thaw(); return; }

				/* SORTIE DE GESTE EN UN SEUL MOUVEMENT. L'ancienne version
				   glissait sur l'inertie PUIS rendait la main au snap — qui
				   recalait le rail d'un sursaut, à contretemps. Ici on fait
				   le travail du snap nous-mêmes : destination projetée par
				   l'inertie (série géométrique de la décroissance 0.92, soit
				   ≈ 11,5 × la vélocité), posée sur le début de carte le plus
				   proche, et un seul trajet décéléré jusque-là. Le snap ne
				   revient qu'une fois le rail posé : rien ne saute. */
				var max = rail.scrollWidth - rail.clientWidth;
				if (velocity > 60) velocity = 60;
				else if (velocity < -60) velocity = -60;
				var projected = rail.scrollLeft + velocity * 11.5;
				if (projected < 0) projected = 0;
				else if (projected > max) projected = max;

				var railLeft = rail.getBoundingClientRect().left;
				var target = projected;
				var best = Infinity;
				Array.prototype.forEach.call(rail.children, function (card) {
					var pos = card.getBoundingClientRect().left - railLeft + rail.scrollLeft;
					if (pos > max) pos = max;
					var d = pos - projected;
					if (d < 0) d = -d;
					if (d < best) { best = d; target = pos; }
				});

				var from = rail.scrollLeft;
				var span = target - from;
				if (span > -1 && span < 1) { thaw(); return; }
				/* Durée liée à l'élan : un lâcher vif porte plus loin et un
				   peu plus longtemps, un lâcher posé s'arrête court. */
				var dur = 320 + Math.min(velocity < 0 ? -velocity : velocity, 60) * 3;
				var t0 = null;
				(function settle(now) {
					momentum = null;
					if (t0 === null) t0 = now || performance.now();
					var t = ((now || performance.now()) - t0) / dur;
					if (t > 1) t = 1;
					/* Décélération cubique : mêmes sorties franches que le kit. */
					var eased = 1 - (1 - t) * (1 - t) * (1 - t);
					rail.scrollLeft = from + span * eased;
					if (t < 1) {
						momentum = requestAnimationFrame(settle);
					} else {
						thaw();
					}
				})();
			}
			rail.addEventListener('pointerup', release);
			rail.addEventListener('pointercancel', release);

			/* Le clic qui conclut un tirage n'est pas un clic. Phase de
			   capture : il doit tomber avant le « lien cliqué → fermer le
			   tiroir » posé plus haut sur le tiroir. */
			rail.addEventListener('click', function (event) {
				if (!dragged) return;
				dragged = false;
				event.preventDefault();
				event.stopPropagation();
			}, true);
		});
	}

	/* -----------------------------------------------------------------------
	   MÉDIAS DIFFÉRÉS
	   ---------------------------------------------------------------------
	   Les visuels des volets ne sont demandés qu'à la première ouverture. Sur
	   un menu présent sur toutes les pages, sept images d'expertise, deux
	   médias de projet et quatre logos chargés d'office se payent au LCP.
	   ----------------------------------------------------------------------- */

	function hydrate(scope) {
		if (scope.dataset.mmHydrated === '1') return;
		scope.dataset.mmHydrated = '1';

		Array.prototype.forEach.call(scope.querySelectorAll('[data-mm-src]'), function (node) {
			var src = node.getAttribute('data-mm-src');
			var srcset = node.getAttribute('data-mm-srcset');
			node.removeAttribute('data-mm-src');
			if (srcset) {
				node.setAttribute('srcset', srcset);
				node.removeAttribute('data-mm-srcset');
			}
			if (src) node.setAttribute('src', src);
			if (node.tagName === 'VIDEO') node.load();
		});
	}

	function playMedia(scope) {
		Array.prototype.forEach.call(scope.querySelectorAll('video'), function (video) {
			var attempt = video.play();
			if (attempt && attempt.catch) attempt.catch(function () { /* autoplay refusé : l'affiche suffit */ });
		});
	}

	function pauseMedia(scope) {
		Array.prototype.forEach.call(scope.querySelectorAll('video'), function (video) {
			if (!video.paused) video.pause();
		});
	}

	/* ----------------------------------------------------------------------- */

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () { init(); });
	} else {
		init();
	}

	/* Éditeur de thème : la section est réinjectée à chaque réglage. */
	document.addEventListener('shopify:section:load', function (event) {
		var header = event.target.querySelector
			? (event.target.matches && event.target.matches('header.mm-section-header')
				? event.target
				: event.target.querySelector('header.mm-section-header'))
			: null;
		if (header) init(header);
	});
	document.addEventListener('shopify:section:unload', function () {
		document.documentElement.classList.remove('stop-scrolling');
		var scrim = document.querySelector('.mm-nav-scrim');
		if (scrim) scrim.classList.remove('is-visible');
	});
})();
